import ERRORS from 'constants/errors';
import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import ButtonPrimary from 'components/shared/ButtonPrimary';
import ButtonSecondary from 'components/shared/ButtonSecondary';
import { Trans, useTranslation } from 'react-i18next';
import { getCountryCallingCodeOptions, COUNTRY_CALLING_CODES } from '@nbx/frontend-helpers/misc';
import { PhoneNumberField, Field } from '@nbx/ui-components';
import { NbxCapacitorFirebaseAuth } from '@nbx/capacitor-firebase-auth';
import { RecaptchaVerifierComponent } from './RecaptchaVerifierComponent';
import styles from './AddTwoFactorAuthSMS.module.scss';

const TEST_NUMBER = '+11231231234';
const SMS_CODE_TIMEOUT_MS = 20000;

const countryCallingCodeOptions = getCountryCallingCodeOptions();

export interface AddTwoFactorAuthSMSProps {
  titleMsg?: string;
  onClose?: () => void;
  onEnrollSuccess?: () => void;
  onSkip?: () => void;
  onError?: (errMsg: string) => void;
  isMobile?: boolean;
  loading?: boolean;
  onRequiresRecentLogin?: ({
    onClose
  }: {
    onClose: ({ isSuccessful }: { isSuccessful: boolean }) => void;
  }) => void;
  sendSMSButtonDisabled?: boolean;
}

export const AddTwoFactorAuthSMS = ({
  titleMsg,
  onClose,
  onSkip,
  onEnrollSuccess,
  onError,
  isMobile,
  loading,
  onRequiresRecentLogin,
  sendSMSButtonDisabled = false
}: AddTwoFactorAuthSMSProps) => {
  const { t } = useTranslation();
  const [currentUser, setCurrentUser] = useState();
  const [isLoading, setIsLoading] = useState();
  const [verificationId, setVerificationId] = useState();
  const [code, setCode] = useState();
  const [errorMsg, setErrorMsg] = useState();
  const [phoneCountry, setPhoneCountry] = useState('NO');
  const [phoneNumber, setPhoneNumber] = useState();
  const [phoneNumberWithCallingCode, setPhoneNumberWithCallingCode] = useState();
  const [enrollMultiFactorUserResult, setEnrollMultiFactorUserDone] = useState<boolean>();

  const [renderRecaptchaVerifier, setRenderRecaptchaVerifier] = useState();
  const [recaptchaVerifier, setRecaptchaVerifier] = useState();

  const [isSMSTimeout, setIsSMSTimeout] = useState(false);
  const [hasBeenResent, setHasBeenResent] = useState(false);
  const resendTimeout = useRef();

  const [requiresRecentLogin, setRequiresRecentLogin] = useState();

  const initCurrentUser = useCallback(async () => {
    const firebaseAuth = await window.FIREBASE.importAuth();
    const auth = await firebaseAuth.getAuth();
    setCurrentUser(await window.FIREBASE.user());
    return auth.onAuthStateChanged(async user => {
      if (user) setCurrentUser(user);
    });
  }, []);

  useEffect(() => {
    if (currentUser === null) setErrorMsg(ERRORS.authRequiresRecentLogin);
  }, [currentUser]);

  useEffect(() => {
    const callingCode = COUNTRY_CALLING_CODES.other[phoneCountry]
      ? COUNTRY_CALLING_CODES.other[phoneCountry]
      : COUNTRY_CALLING_CODES.scandinavian[phoneCountry];
    phoneNumber &&
      setPhoneNumberWithCallingCode(
        phoneNumber === TEST_NUMBER ? TEST_NUMBER : `+${callingCode}${phoneNumber}`
      );
  }, [phoneCountry, phoneNumber]);

  const handleResendSMSCode = useCallback(() => {
    console.log('handleResendSMSCode: RenderRecaptchaVerifier');
    setRenderRecaptchaVerifier(true);
    resendTimeout.current && clearTimeout(resendTimeout.current);
    setHasBeenResent(true);
    resendTimeout.current = setTimeout(() => {
      setHasBeenResent(false);
      setIsSMSTimeout(true);
    }, SMS_CODE_TIMEOUT_MS);
  }, []);

  const handleReauthenticateClose = useCallback(({ isSuccessful } = { isSuccessful: false }) => {
    console.log('handleReauthenticateClose: isSuccessful = ', isSuccessful);
    setRequiresRecentLogin(false);
    if (isSuccessful) {
      setIsLoading(true);
      console.log('handleReauthenticateClose: will resend sms - handleResendSMSCode');
      setTimeout(handleResendSMSCode, 1000);
    }
  }, []);

  const sendCode = useCallback(
    async (phoneNumber: string, recaptchaVerifier) => {
      console.log(`sending sms code...`);
      setErrorMsg(undefined);
      setIsLoading(true);
      if (!phoneNumber) return;
      try {
        console.log(`recaptchaVerifier`, !!recaptchaVerifier);
        window.dataLayer?.push({
          event: 'enable_firebase_2FA_SMS_send_code'
        });

        if (window.FIREBASE.isNativeAuth()) {
          const { verificationId } = await NbxCapacitorFirebaseAuth.verifyPhoneNumber({ phoneNumber });
          setVerificationId(verificationId);
          console.log('sms has been sent from native, verificationId', verificationId);
        } else {
          const phoneInfoOptions = {
            phoneNumber,
            session: await window.FIREBASE.multiFactorUser().then(user => user.getSession())
          };

          console.log(`Send SMS verification code.`);

          const verificationId = await window.FIREBASE.verifyPhoneNumber({
            phoneInfoOptions,
            recaptchaVerifier
          });
          setVerificationId(verificationId);
          console.log('sms has been sent from web, verificationId', verificationId);
        }

        resendTimeout.current = setTimeout(() => setIsSMSTimeout(true), SMS_CODE_TIMEOUT_MS);
      } catch (e) {
        if (e.code === 'auth/operation-not-allowed') {
          console.warn(e.code, e.message);
          setErrorMsg(ERRORS.authOperationNotAllowed);
        } else if (
          e.code === 'auth/requires-recent-login' ||
          e.code === 'auth-nbx/requires-recent-login' ||
          e.code === 'auth-nbx/user-is-not-authenticated-in-native-firebase' ||
          e.code === 'auth-nbx/users-credential-is-no-longer-valid-login-again'
        ) {
          console.warn('recent login required', e.message);
          setRequiresRecentLogin(true);
          onRequiresRecentLogin
            ? onRequiresRecentLogin({
                onClose: handleReauthenticateClose
              })
            : setErrorMsg('please re-login');
        } else if (e.code === 'auth/invalid-phone-number') {
          console.warn(e);
          setErrorMsg(ERRORS.authInvalidPhoneNumber);
        } else if (e.code === 'auth/unverified-email') {
          console.warn(e);
          setErrorMsg(ERRORS.authUnverifiedEmail);
        } else if (e.code === 'auth/second-factor-already-in-use') {
          console.warn(e);
          setErrorMsg(ERRORS.authSecondFactorAlreadyInUse);
          onError && onError(ERRORS.authSecondFactorAlreadyInUse);
        } else {
          setErrorMsg(`error sending sms ${e?.code ?? ''}`);
          console.error(`AddTwoFactorAuthSMS: error sending sms, error code: ${e?.code}`, e);
        }
      } finally {
        console.log('clear recaptchaVerifier');
        setIsLoading(false);
        recaptchaVerifier && recaptchaVerifier.clear();
        setRenderRecaptchaVerifier(false);
        setRecaptchaVerifier(undefined);
      }
    },
    [onError]
  );

  const verifyCode = useCallback(
    async (verificationId?: string, verificationCode?: string) => {
      if (!verificationId || !verificationCode) return;
      setErrorMsg(undefined);
      setIsLoading(true);
      try {
        await window.FIREBASE.enrollMultiFactorUser({ verificationId, verificationCode });
        setEnrollMultiFactorUserDone(true);
        window.dataLayer?.push({
          event: 'enable_firebase_2FA_SMS_complete'
        });
        onEnrollSuccess && onEnrollSuccess();
      } catch (e) {
        if (e.code === 'auth/second-factor-already-in-use') {
          console.warn(e);
          setErrorMsg(ERRORS.authSecondFactorAlreadyInUse);
          onError && onError(ERRORS.authSecondFactorAlreadyInUse);
        } else if (e.code === 'auth/invalid-verification-code') {
          console.warn(e);
          setErrorMsg(ERRORS.twoFactor);
          onError && onError(ERRORS.twoFactor);
        } else {
          console.error('error enrolling user', e);
          setErrorMsg('error enrolling user');
          onError && onError('error enrolling user');
        }
      } finally {
        setIsLoading(false);
      }
    },
    [onEnrollSuccess, onError]
  );

  const handleSetCode = useCallback(value => {
    setCode(value);
  }, []);

  useEffect(() => {
    initCurrentUser();
  }, [initCurrentUser]);

  useEffect(() => {
    if (recaptchaVerifier) sendCode(phoneNumberWithCallingCode, recaptchaVerifier);
  }, [recaptchaVerifier]);

  useEffect(() => {
    return () => resendTimeout.current && clearTimeout(resendTimeout.current);
  }, []);

  const countryCodeSelectOptions: Array<{ value: string; label: string; disabled?: boolean }> = useMemo(
    () =>
      countryCallingCodeOptions
        ? Object.entries(countryCallingCodeOptions).map(([value, label]) =>
            value === 'divider' ? { ...label } : { value, label }
          )
        : [],
    []
  );

  if (currentUser === undefined) return <nbx-spinner />;

  if (requiresRecentLogin) return null;

  return (
    <div className={styles.twoFactorAuthSMS}>
      {enrollMultiFactorUserResult ? (
        <>
          <nbx-text>
            <Trans i18nKey="auth_2fa_prompt_phone_number_added" />
          </nbx-text>
          {onClose && (
            <ButtonPrimary fullWidth={isMobile} onClick={onClose} loading={loading || isLoading}>
              {t('common_understood')}, {t('common_done')?.toLocaleLowerCase()}
            </ButtonPrimary>
          )}
        </>
      ) : (
        <>
          {renderRecaptchaVerifier && (
            <RecaptchaVerifierComponent
              onRender={setRecaptchaVerifier}
              onError={e => {
                console.error(e);
                setErrorMsg('error getting captcha');
              }}
            />
          )}

          {!verificationId && <nbx-text>{titleMsg ?? t('auth_2fa_signup_prompt_phone_number')}</nbx-text>}
          {verificationId && (
            <nbx-text>{t('auth_2fa_prompt_code_sms_2fa_setup', { phone: phoneNumber })}</nbx-text>
          )}
          {!verificationId && (
            <div className={styles.formItem}>
              <PhoneNumberField
                phoneNumber={phoneNumber}
                countryCode={phoneCountry}
                countryCodeOptions={countryCodeSelectOptions}
                onPhoneNumberChange={setPhoneNumber}
                onCountryCodeChange={setPhoneCountry}
                onError={console.error}
                placeholder={t('verif_personal_phone')}
                // label={'phoneNumber'}
                id={'signupPhoneNumber'}
                label={''}
              />
              <ButtonPrimary
                fullWidth={isMobile}
                disabled={
                  !phoneNumber || sendSMSButtonDisabled || errorMsg === ERRORS.authSecondFactorAlreadyInUse
                }
                onClick={() => !!currentUser && setRenderRecaptchaVerifier(true)}
                loading={loading || isLoading}>
                {t('common_send')} {t('common_sms')} {t('common_code')}
              </ButtonPrimary>
            </div>
          )}
          {verificationId && (
            <div className={styles.formItem}>
              <Field
                autoFocus
                id="sms-code"
                label="SMS Code"
                isShowLabel={false}
                type={'tel'}
                maxLength={6}
                onValueChange={handleSetCode}
                value={code}
                size="small"
              />

              <ButtonPrimary
                // fullWidth={isMobile}
                onClick={() => verifyCode(verificationId, code)}
                loading={loading || isLoading}>
                {t('common_verify')}
              </ButtonPrimary>

              {isSMSTimeout && (
                <>
                  <nbx-text muted>
                    {hasBeenResent ? t('auth_2fa_resend_sms_success') : t('auth_2fa_resend_sms_prompt')}
                  </nbx-text>
                  <ButtonSecondary
                    onClick={handleResendSMSCode}
                    disabled={loading || hasBeenResent}
                    loading={loading || isLoading}>
                    {t('auth_2fa_resend_sms')}
                  </ButtonSecondary>
                </>
              )}
            </div>
          )}
          <nbx-form-error>{t(errorMsg)}</nbx-form-error>
        </>
      )}
      {!verificationId && !titleMsg && (
        <nbx-text muted legal>
          {t('auth_2fa_signup_prompt_phone_number_or_app')}
        </nbx-text>
      )}
      {!verificationId && onSkip && (
        <nbx-button-text arrow legal muted onClick={onSkip}>
          {t('common_not_now')}
        </nbx-button-text>
      )}
    </div>
  );
};
